import {
  WORLD_COUNTRIES,
  getStripeConnectSupportedCountryName,
  isStripeConnectSupportedCountry,
  normalizeStripeConnectCountryName,
} from "./stripeConnectCountries";

export {
  WORLD_COUNTRIES,
  getStripeConnectSupportedCountryName,
  isStripeConnectSupportedCountry,
};

export type DiscoverScopeOption = "nearbyCountry" | "country" | "continent" | "world";

export type DiscoverContinentId =
  | "africa"
  | "asia"
  | "europe"
  | "northAmerica"
  | "southAmerica"
  | "oceania";

export const DISCOVER_SCOPE_OPTIONS: Array<{
  id: DiscoverScopeOption;
  title: string;
}> = [
  { id: "nearbyCountry", title: "Nearby" },
  { id: "country", title: "Country" },
  { id: "continent", title: "Continent" },
  { id: "world", title: "World" },
];

export const DISCOVER_CONTINENTS: Array<{
  id: DiscoverContinentId;
  title: string;
}> = [
  { id: "africa", title: "Africa" },
  { id: "asia", title: "Asia" },
  { id: "europe", title: "Europe" },
  { id: "northAmerica", title: "North America" },
  { id: "southAmerica", title: "South America" },
  { id: "oceania", title: "Oceania" },
];

export const normalizeDiscoverCountryName = normalizeStripeConnectCountryName;

const northAmericaCountries = new Set(
  [
    "united states",
    "usa",
    "us",
    "canada",
    "mexico",
    "costa rica",
    "panama",
    "el salvador",
    "guatemala",
    "dominican republic",
    "jamaica",
    "bahamas",
    "saint lucia",
    "st lucia",
    "antigua and barbuda",
    "trinidad and tobago",
  ].map(normalizeDiscoverCountryName)
);

const southAmericaCountries = new Set(
  [
    "argentina",
    "colombia",
    "peru",
    "chile",
    "ecuador",
    "bolivia",
    "paraguay",
    "uruguay",
    "guyana",
  ].map(normalizeDiscoverCountryName)
);

const europeCountries = new Set(
  [
    "albania",
    "austria",
    "belgium",
    "bosnia and herzegovina",
    "bulgaria",
    "cyprus",
    "czech republic",
    "czechia",
    "denmark",
    "estonia",
    "finland",
    "france",
    "germany",
    "greece",
    "hungary",
    "iceland",
    "ireland",
    "italy",
    "latvia",
    "lithuania",
    "luxembourg",
    "malta",
    "moldova",
    "monaco",
    "netherlands",
    "north macedonia",
    "norway",
    "poland",
    "portugal",
    "romania",
    "serbia",
    "slovakia",
    "slovenia",
    "spain",
    "sweden",
    "switzerland",
    "united kingdom",
  ].map(normalizeDiscoverCountryName)
);

const asiaCountries = new Set(
  [
    "united arab emirates",
    "armenia",
    "bahrain",
    "brunei",
    "cambodia",
    "hong kong",
    "hong kong sar china",
    "israel",
    "japan",
    "jordan",
    "kuwait",
    "macao sar china",
    "macau",
    "macao",
    "mongolia",
    "oman",
    "pakistan",
    "philippines",
    "qatar",
    "saudi arabia",
    "singapore",
    "south korea",
    "korea republic of",
    "korea south",
    "sri lanka",
    "taiwan",
    "thailand",
    "turkey",
    "turkiye",
    "uzbekistan",
    "vietnam",
  ].map(normalizeDiscoverCountryName)
);

const africaCountries = new Set(
  [
    "benin",
    "botswana",
    "cote d'ivoire",
    "côte d’ivoire",
    "egypt",
    "ethiopia",
    "gambia",
    "ghana",
    "kenya",
    "madagascar",
    "mauritius",
    "morocco",
    "namibia",
    "nigeria",
    "rwanda",
    "senegal",
    "south africa",
    "tanzania",
    "tunisia",
  ].map(normalizeDiscoverCountryName)
);

const oceaniaCountries = new Set(
  [
    "australia",
    "new zealand",
  ].map(normalizeDiscoverCountryName)
);

export const discoverContinentContainsCountry = (
  continent: DiscoverContinentId,
  countryName: string
): boolean => {
  const normalizedCountryName = normalizeDiscoverCountryName(countryName);
  if (!normalizedCountryName) {
    return false;
  }

  switch (continent) {
    case "africa":
      return africaCountries.has(normalizedCountryName);
    case "asia":
      return asiaCountries.has(normalizedCountryName);
    case "europe":
      return europeCountries.has(normalizedCountryName);
    case "northAmerica":
      return northAmericaCountries.has(normalizedCountryName);
    case "southAmerica":
      return southAmericaCountries.has(normalizedCountryName);
    case "oceania":
      return oceaniaCountries.has(normalizedCountryName);
    default:
      return false;
  }
};

export const discoverContinentFromCountry = (
  countryName: string
): DiscoverContinentId | null => {
  const normalizedCountryName = normalizeDiscoverCountryName(countryName);
  if (!normalizedCountryName) {
    return null;
  }

  if (africaCountries.has(normalizedCountryName)) {
    return "africa";
  }

  if (asiaCountries.has(normalizedCountryName)) {
    return "asia";
  }

  if (europeCountries.has(normalizedCountryName)) {
    return "europe";
  }

  if (northAmericaCountries.has(normalizedCountryName)) {
    return "northAmerica";
  }

  if (southAmericaCountries.has(normalizedCountryName)) {
    return "southAmerica";
  }

  if (oceaniaCountries.has(normalizedCountryName)) {
    return "oceania";
  }

  return null;
};
